import './language-picker-options.scss'
import { useInitialData } from '@common/hooks/useInitialData'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { getLanguageTag, setLanguageTag } from '@vista-digital/occ-domain/src'
import { Dropdown, DropdownMode } from '@vista-digital/occ-ui/src'
import type { IndexKey } from '@vista-digital/utilities/src'
import { type FC, useEffect } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { Pages } from '@/content/pages'
import { navigationService } from '@/content/services/navigation.service'
import { useUrlService } from '@/content/services/omnia-service-context'

export const LanguagePickerOptions: FC = () => {
    const logger = useLogger('LanguagePickerOptions')
    const urlService = useUrlService()
    const { search } = useLocation()
    const { culture, displayLanguagePicker, languagePickerConfiguration } = useInitialData()

    // Align the session languageTag with the current page culture
    useEffect(() => {
        if (culture !== getLanguageTag()) {
            setLanguageTag(culture)
            logger.debug(`Synchronized session languageTag to culture: ${culture}`)
        }
    }, [culture, logger])

    if (!languagePickerConfiguration || !displayLanguagePicker) return null

    const languages = languagePickerConfiguration.map(lang => ({
        text: lang.longName,
        value: lang.languageTag,
        shortName: lang.shortName,
    }))

    const changeLanguage = (value: IndexKey) => {
        // Prevent bfcache from restoring stale content when user navigates back (Safari mobile)
        window.addEventListener('pageshow', (event: PageTransitionEvent) => {
            if (event.persisted) {
                window.location.reload()
            }
        })

        const version = urlService.getVersion(value.toString().toLowerCase())
        if (version) {
            const url = new URL(version)
            url.search = search
            // eslint-disable-next-line no-restricted-properties -- This is a redirect
            window.location.assign(url.toString())
            return
        }

        // Use the backend as a fallback
        // TODO: This fallback doesn't preserve query params currently. Should it? Depends on when this has to be used.
        const page = navigationService.getCurrentPage()
        if (!page) return
        const pageDetails = Pages[page]
        const pageUrl = pageDetails.customAlias ? navigationService.getPageUrl(page) : ''
        window.location.assign(
            // eslint-disable-next-line no-restricted-properties -- This is a redirect
            `/api/omnia/v1/language/change?culture=${value}&friendly=${window.location.pathname}&pageUrl=${pageUrl}`
        )
    }
    const getPreviewTemplate = (value: IndexKey) => {
        return languages.find(option => option.value === value)?.shortName.toUpperCase() || culture
    }

    return (
        <Dropdown
            id="language-dropdown-container"
            className="language-dropdown"
            mode={DropdownMode.Normal}
            options={languages}
            optionPreviewTemplate={option => <>{getPreviewTemplate(option.value)}</>}
            value={culture}
            onChangeValue={changeLanguage}
            enableSearch={false}
            children={<div className={'dropdown-display-triangle'} />}
        />
    )
}
